import type { Editor } from '@tiptap/react';

import { Popover } from '@base-ui/react';
import { getMarkRange, getMarkType, useEditorState } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { ExternalLink } from '../Link/ExternalLink';
import { hrefStyle, iconStyle, popupStyle, positionerStyle, removeButtonStyle } from './LinkPopover.css';

type Props = {
  editor: Editor;
};

export const LinkPopover = ({ editor }: Props) => {
  const activeLink = useEditorState({
    editor,
    selector: (snapshot) => {
      // A destroyed editor has no schema left, yet React can still render with it while the editor is being re-created.
      if (snapshot.editor.isDestroyed) return null;

      const { $from, from } = snapshot.editor.state.selection;
      const markRange = getMarkRange($from, getMarkType('link', snapshot.editor.schema));
      const { href } = snapshot.editor.getAttributes('link') as { href?: string };

      if (!markRange || !href) return null;

      return { caretPosition: from, href, markStart: markRange.from };
    },
  });

  const { t } = useTranslation(['common']);

  // Hiding the popup is remembered per caret position, so that it shows up again as soon as the caret moves.
  const [dismissedPosition, setDismissedPosition] = useState<number>();

  // Coming back to the editor is a fresh start — whatever hid the popup before shouldn't keep it hidden.
  useEffect(() => {
    const clearDismissedPosition = () => setDismissedPosition(undefined);

    editor.on('focus', clearDismissedPosition);

    return () => {
      editor.off('focus', clearDismissedPosition);
    };
  }, [editor]);

  // Without an anchor the popup would end up in the top left corner of the page, so rather show nothing at all.
  const anchor = activeLink === null ? null : findLinkElement(editor, activeLink.markStart);

  // Base UI keeps the popup mounted while it fades out, but by then the caret already left the link. Hold on to what
  // was shown last so the popup keeps its content and position instead of collapsing into an empty box mid-animation.
  const shownLinkRef = useRef<{ anchor: HTMLAnchorElement; href: string } | null>(null);

  if (activeLink !== null && anchor !== null) shownLinkRef.current = { anchor, href: activeLink.href };

  const removeLink = () =>
    editor.state.selection.empty
      ? editor.chain().focus().extendMarkRange('link').unsetLink().run()
      : editor.chain().focus().unsetLink().run();

  return (
    <Popover.Root
      onOpenChange={(open, { event, reason }) => {
        if (open) return;

        // Base UI reports a press inside the editor as an outside press, but that's exactly when the popup should show
        // up or follow the caret to another link. Only a press that leaves the editor hides it — as does escape.
        const isPressInsideEditor = reason === 'outside-press' && event.target instanceof Node && editor.view.dom.contains(event.target);

        if (!isPressInsideEditor) setDismissedPosition(activeLink?.caretPosition);
      }}
      open={anchor !== null && activeLink?.caretPosition !== dismissedPosition}
    >
      <Popover.Portal>
        <Popover.Positioner
          align="start"
          anchor={shownLinkRef.current?.anchor ?? null}
          className={positionerStyle}
          collisionPadding={8}
          side="bottom"
          sideOffset={4}
        >
          {/* The caret has to stay where it is, so the popup never takes focus away from the editor. Nor does it hand
              focus back when closing: that would refocus the editor after a press outside it, reopening the popup. */}
          <Popover.Popup className={popupStyle} finalFocus={false} initialFocus={false}>
            <Icon className={iconStyle} name="Globe" size={20} />

            <ExternalLink className={hrefStyle} href={shownLinkRef.current?.href}>
              {shownLinkRef.current?.href}
            </ExternalLink>

            <Button aria-label={t('common:richText.toolbar.unlink')} className={removeButtonStyle} icon="Trash" onClick={removeLink} />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};

const findLinkElement = (editor: Editor, markStart: number) => {
  const { node } = editor.view.domAtPos(markStart + 1);
  const element = node instanceof Element ? node : node.parentElement;

  return element?.closest('a') ?? null;
};
